import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import { useMediaQuery } from "react-responsive";
import { Dropdown } from "react-bootstrap";
import BackButton from "../assets/icons/back_button.svg";
import { barcodeDataApi } from "../utils/constants";
import useApis from "../services/useAPI";
import { MarkerScaleGraph } from "./Bloods/Reports/MarkerScale/MarkerScaleGraph";
import MarkerGraph from "./Bloods/Reports/MarkerScale/MarkerGraph";

const ChevronDownIcon = () => {
  return (
    <div className="icon-wrapper">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 640"
        width="16"
        height="16"
      >
        <path d="M297.4 470.6C309.9 483.1 330.2 483.1 342.7 470.6L534.7 278.6C547.2 266.1 547.2 245.8 534.7 233.3C522.2 220.8 501.9 220.8 489.4 233.3L320 402.7L150.6 233.4C138.1 220.9 117.8 220.9 105.3 233.4C92.8 245.9 92.8 266.2 105.3 278.7L297.3 470.7z" />
      </svg>
    </div>
  );
};

const ResultByBiomarker = (props) => {
  const isCustoMobile = useMediaQuery({ maxWidth: 576 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [sortBy, setSortBy] = useState("A-Z");
  const [searchText, setSearchText] = useState("");
  const [biomarkers, setBiomarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const wrapperRef = useRef(null);
  const { callApi, resultGet, isLoading } = useApis();

  const getMarkerStatus = (marker) => {
    if (marker?.name === "Vitamin B12 Active") {
      return getResultStatus(marker?.value);
    }
    return marker?.result;
  };

  const sortBiomarkers = (data, sortType) => {
    const statusOrders = {
      "High to Low": { "abnormal_high": 1, high: 2, normal: 3, "low-normal": 4, low: 5, "abnormal_low": 6 },
      "Low to High": { "abnormal_low": 1,low: 2, "low-normal": 3, normal: 4, high: 5, "abnormal_high": 6 },
    };

    const getStatusOrder = (marker) => {
      const status = getMarkerStatus(marker)?.toLowerCase();
      return statusOrders[sortType]?.[status] ?? 7;
    };

    return [...data].sort((a, b) => {
      const orderA = getStatusOrder(a);
      const orderB = getStatusOrder(b);
      if (orderA !== orderB) return orderA - orderB;
      return sortType === "Z-A" ? b?.name?.localeCompare(a?.name) : a?.name?.localeCompare(b?.name);
    });
  };

  useEffect(() => {
    if (props?.data) {
      let filteredData = props.data;
      if (searchText) {
        filteredData = props.data.filter((marker) =>
          marker?.name?.toLowerCase().includes(searchText.toLowerCase()),
        );
      }
      setBiomarkers(sortBiomarkers(filteredData, sortBy));
    }
  }, [props, sortBy, searchText]);

  const handleSortBy = (d) => {
    setSortBy(d);
  };


  const getResultStatus = (value) => {
    if (value > 150) {
      return "High";
    } else if (value > 70 && value <= 150) {
      return "Normal";
    } else if (value >= 37.5 && value <= 70) {
      return "Low-Normal";
    } else if (value < 37.5) {
      return "Low";
    }
    return null;
  };

  const getCategoryColor = (timsStatus) => {
    const colorMap = {
      low: "#E9CDCC",
      "low normal": "#F8E5C1",
      "low-normal": "#FEF3B0",
      "abnormal_low": "#E9CDCC",
      normal: "#D1E6E2",
      high: "#D9808A",
      "abnormal_high": "#D9808A",
    };

    return colorMap[timsStatus?.toLowerCase()] || "#FFFFFF";
  };


  const toTitleCase = (str) => {
    return str
      ?.toLowerCase()
      ?.split(" ")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ")
      ?.replaceAll("_", " ")
      ?.replaceAll("-", " ");
  };

  const handleBiomarkerClick = (marker) => {
    
    setSelectedMarker(marker);
    
    if (marker.code) {
      let code = marker.code;
      if (code === "HbA1c") {
        code = "HBA1c";
      }
      callApi(null, barcodeDataApi(code), "GET");
    }

    if (wrapperRef.current) {
      setTimeout(() => {
        wrapperRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  useEffect(() => {
  if (selectedMarker && resultGet) {
    const enrichedMarker = {
      ...selectedMarker,
      boundary: 
        resultGet?.metadata?.boundary || null,      
      lower_boundary: 
        resultGet?.metadata?.lower_boundary || null,
      upper_boundary: 
        resultGet?.metadata?.upper_boundary || null,
      type: 
        resultGet?.metadata?.type || 'normal-high',
      decision_type: 
        resultGet?.metadata?.decision_type || 'threshold',
    };
    
    setSelectedMarker(enrichedMarker);
  }
}, [resultGet])

  const handleBack = () => {
    setSelectedMarker(null);
  };

  if (selectedMarker) {
    return (
      <div ref={wrapperRef} className="result-by-biomarker-detail">
        <div className="biomarker-detail-header d-flex align-items-center horizontal-section-biomarker-title gap-2 mb-4 mt-5">
          <button
            className="navitem__back-btn"
            onClick={handleBack}
            aria-label="Back to biomarker list"
            style={{
              background: "none",
              border: "none",
              padding: "0.5rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img src={BackButton} alt="back button" width="24" height="24" />
          </button>
          <h3 className="mb-0" style={{ fontSize: "1.5rem", fontWeight: "600" }}>
            {selectedMarker.name}
          </h3>
        </div>

        <MarkerScaleGraph
          marker={selectedMarker}
          isScale={false}
          graphContent={
            <MarkerGraph
              marker={selectedMarker}
              reportedDate={resultGet?.data[0].reported_date}
              currentValue={selectedMarker.value}
              historicalData={resultGet?.data || []}
              isMobileView={isMobile}
            />
          }
        />
      </div>
    );
  }

  // Original list view
  return (
    <div ref={wrapperRef} className="result-by-biomarkers d-flex flex-column">
      <div
        className={`result-by-biomarker-btns mb-3 d-flex justify-content-between ${isCustoMobile && "flex-column gap-3"}`}
      >
        <div className="search-biomarker">
          <InputGroup
            className="rounded-pill border px-3"
            style={{ background: "#fff" }}
          >
            <Form.Control
              type="text"
              placeholder="Search"
              className="border-0 shadow-none bg-transparent"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <InputGroup.Text className="border-0 bg-transparent ps-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="#003032"
                viewBox="0 0 16 16"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" />
              </svg>
            </InputGroup.Text>
          </InputGroup>
        </div>
        <div className="sort-by-biomarkers status-date d-flex">
          <Dropdown>
            <Dropdown.Toggle className="my-result-status-btn">
              <div className="d-flex align-items-center" style={{padding: "3px 0 0 0"}} title={sortBy}>
                Sort by:{" "}
                <span className="text-pink input-label ms-1">{sortBy}</span>
              </div>
              <ChevronDownIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {["A-Z", "Z-A", "High to Low", "Low to High"].map((d) => (
                <Dropdown.Item key={d} onClick={() => handleSortBy(d)}>
                  {d}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div className="card container-fluid">
        <table className="table">
          <tbody>
            {biomarkers.length > 0 ? (
              biomarkers?.map((marker, index) => (
                <tr
                  key={marker?.id ?? marker?.name}
                  className="cursor-pointer-cls"
                  onClick={() => handleBiomarkerClick(marker)}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(0, 48, 50, 0.04)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <td
                    className={`row test-list-row first-td ${isCustoMobile ? "flex-column" : ""} ${index === 0 ? "first-td" : ""} ${index === biomarkers?.length - 1 ? "last-td" : ""} d-flex rounded-6`}
                  >
                    <div
                      className={`col-sm-10 ageo-bold fs-16 text-teal align-self-center ${isCustoMobile && "mb-3 fs-18"}`}
                    >
                      {marker?.name}
                    </div>
                    <div
                      className={`col-sm-2 d-flex align-items-center justify-content-${props?.isTablet ? "end" : "start"}`}
                    >
                      <div
                        className="test-status-my-result d-flex align-items-center justify-content-center"
                        style={{
                          backgroundColor:
                            marker?.name === "Vitamin B12 Active"
                              ? getCategoryColor(
                                  getResultStatus(marker?.value),
                                )
                              : getCategoryColor(marker?.result),
                        }}
                      >
                        {marker?.name === "Vitamin B12 Active"
                          ? toTitleCase(getResultStatus(marker?.value))
                          : toTitleCase(marker?.result)}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <div className="result-not-found">
                <p>No result found</p>
              </div>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ResultByBiomarker.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      code: PropTypes.string,
      value: PropTypes.number,
      result: PropTypes.string,
      details: PropTypes.object,
    }),
  ),
  isTablet: PropTypes.bool,
};

export default ResultByBiomarker;